/**
 * Step 8 — Oversight & Vendors.
 *
 * APPENDED, like step 7, for the reason given in that file's header: the wizard's step numbers
 * are positional in the URL and there is no redirect table.
 *
 * Four blocks that the ported register had no field for at all:
 *
 *   1. **Human oversight (Art 14/26)** — the module's sharpest inconsistency was defining
 *      `agentic-ai` as AI that acts "without a human approving each step" while asking nothing
 *      about who could stop it. The three controls are separate capabilities, not a scale: a
 *      kill switch answers "can we stop it", which is a different question from "can we stop it
 *      doing this one thing to this one person".
 *   2. **Vendors (FR-11)** — replaces the single free-text `thirdPartyDetails` paragraph in
 *      which model, vendor, hosting region, sub-processors, DPA reference and contract expiry
 *      all collapsed into one unqueryable blob. `thirdPartyDetails` still exists on step 5 and
 *      is left alone: existing records hold prose there.
 *   3. **Transfers** — the ROPA's `TransferRecord`, vocabulary and activation rule, reused
 *      rather than re-invented, so a transfer means the same thing in both registers.
 *   4. **Monitoring (Art 72) and periodic review** — nothing brought an active use case back
 *      around; once live it was never looked at again.
 *
 * Effort scales with risk (NFR-02), and the scaling mirrors the server's `validate_ai_act`
 * exactly:
 *   - `high_risk` → a named accountable person AND at least one oversight control are
 *     REQUIRED (`lambda_function.py:868-877`). The block is expanded and marked required.
 *   - `limited` → expanded, recommended, gates nothing.
 *   - `minimal` → collapsed by default and gates nothing. The Act asks nothing extra of a
 *     minimal-risk tool and neither does this step.
 *   - `prohibited` → the shell blocks navigation before this step is reachable at all.
 *
 * Nothing in this step gates on a list being non-empty. `useRepeatableRows` materialises its
 * blank row into the record from an effect as soon as the step mounts (`RepeatableRows.tsx:74-76`),
 * so `vendors.length > 0` would be true the instant the user arrived and would prove nothing.
 * The server drops those blank rows on write, using the same emptiness rules applied here.
 */
import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import { useQuery } from '@tanstack/react-query';
import {
  RepeatableRowCard,
  newRowId,
  useRepeatableRows,
} from '../../../components/RepeatableRows';
import { peopleApi } from '../../../services/api';
import { reactSelectStyles } from '../../../utils/reactSelectStyles';
import {
  HOSTING_REGION_SUGGESTIONS,
  HUMAN_OVERSIGHT_CONTROLS,
  MONITORING_FIELDS,
  REVIEW_FREQUENCIES,
  REVIEW_FREQUENCY_DAYS,
  REVIEW_FREQUENCY_LABELS,
  REVIEW_FREQUENCY_MONTHS,
  SUGGESTED_REVIEW_FREQUENCY,
  TRANSFER_MECHANISMS,
  TRANSFER_MECHANISM_LABELS,
  TRANSFER_MECHANISM_TONE,
  emptyHumanOversight,
  emptyMonitoring,
  hasAnyOversight,
} from '../../../constants/aiAct';
import type {
  HumanOversight,
  Monitoring,
  Person,
  ReviewFrequency,
  TransferMechanism,
  TransferRecord,
  VendorRecord,
} from '../../../types/models';
import type { StepProps } from './types';

/** Required marker. Decorative — the accessible signal is `required` on the control itself. */
const Req: React.FC = () => (
  <span className="text-danger" aria-hidden="true">
    {' '}
    *
  </span>
);

const HOSTING_REGION_LIST_ID = 'ai-vendor-hosting-region-suggestions';

const todayIso = (): string => new Date().toISOString().slice(0, 10);

/** Last day-of-month (0-indexed month), for clamping e.g. Jan 31 + 1 month -> Feb 28, not Mar 3. */
const daysInMonth = (year: number, month0: number): number =>
  new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();

/**
 * Copied from `CollectionScheduleCard.tsx:50-64` — UTC-only arithmetic throughout, because
 * `new Date('2026-08-03')` parses as UTC midnight while `setDate()`/`setMonth()` operate in
 * LOCAL time, so mixing them silently drifts the result by a day across a DST transition.
 * The variant in `MaintenanceTasks.tsx:80-91` does exactly that and has the bug; do not copy it.
 *
 * Mirrors `add_interval` in the AI use case Lambda, which seeds `nextReviewDate` on approval
 * when a cadence is set and no date was chosen — the two must agree or the date moves under
 * the user at approval time.
 */
const addInterval = (fromDateStr: string, frequency: ReviewFrequency): string => {
  const base =
    fromDateStr && !Number.isNaN(new Date(fromDateStr).getTime()) ? fromDateStr : todayIso();
  const [y, m, d] = base.split('-').map(Number);
  const days = REVIEW_FREQUENCY_DAYS[frequency];
  if (days) {
    return new Date(Date.UTC(y, m - 1, d) + days * 86400000).toISOString().slice(0, 10);
  }
  const months = REVIEW_FREQUENCY_MONTHS[frequency];
  if (months) {
    const totalMonths = m - 1 + months;
    const year = y + Math.floor(totalMonths / 12);
    const month0 = ((totalMonths % 12) + 12) % 12;
    const day = Math.min(d, daysInMonth(year, month0));
    return new Date(Date.UTC(year, month0, day)).toISOString().slice(0, 10);
  }
  return base;
};

const blankVendor = (): VendorRecord => ({
  id: newRowId(),
  name: '',
  modelName: '',
  hostingRegion: '',
  dpaReference: '',
  contractExpiry: '',
  trainsOnOurData: false,
  subProcessors: '',
});

/**
 * A vendor row is nothing without a supplier or a model — which is the server's own drop rule
 * (`normalise_vendors`). Matching it here means the "Add vendor" control and the stored record
 * agree on what counts as a row, instead of the editor believing in a row the API discards.
 */
const isBlankVendor = (v: VendorRecord): boolean => !v.name.trim() && !v.modelName.trim();

/** A row the user has clearly started but which the server would still drop. Worth saying out
 *  loud rather than letting the content vanish on save. */
const isOrphanVendor = (v: VendorRecord): boolean =>
  isBlankVendor(v) &&
  !!(
    v.hostingRegion.trim() ||
    v.dpaReference.trim() ||
    v.contractExpiry.trim() ||
    v.subProcessors.trim() ||
    v.trainsOnOurData
  );

const blankTransfer = (): TransferRecord => ({
  id: newRowId(),
  country: '',
  recipient: '',
  mechanism: 'none',
  safeguards: '',
});

/** Same rule as the ROPA (`ProcessingActivityDetail.tsx:200`) and as the server. */
const isBlankTransfer = (t: TransferRecord): boolean =>
  !t.country.trim() && !t.recipient.trim();

const Step8Oversight: React.FC<StepProps> = ({ useCase, update }) => {
  const classification = useCase.aiActClassification || '';
  const isHighRisk = classification === 'high_risk';
  const isMinimal = classification === 'minimal';

  const oversight: HumanOversight = useCase.humanOversight ?? emptyHumanOversight();
  const monitoring: Monitoring = useCase.monitoring ?? emptyMonitoring();

  // Collapsed only where the block is genuinely optional. Opened once by the user it stays open
  // for the life of the step — this is disclosure, not a preference worth persisting.
  const [oversightOpen, setOversightOpen] = useState(!isMinimal);

  const patchOversight = (changes: Partial<HumanOversight>) =>
    update({ humanOversight: { ...oversight, ...changes } });

  const patchMonitoring = (changes: Partial<Monitoring>) =>
    update({ monitoring: { ...monitoring, ...changes } });

  // Shares the ['people'] cache with RaciAssignments, so this costs nothing on a page that has
  // already loaded them.
  //
  // The Person type's personName field is a legacy/fallback shape - the real People-register
  // records carry firstName/lastName instead (confirmed live against the DynamoDB table), so
  // this queries with a locally-widened type rather than trusting personName alone, which would
  // render every option blank.
  const { data: people = [] } = useQuery<Array<Person & { firstName?: string; lastName?: string }>>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });

  const personOptions = useMemo(
    () =>
      people
        .map(p => ({
          value: p.personId,
          label: `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.personName || p.personId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [people],
  );

  const selectedPerson =
    personOptions.find(o => o.value === oversight.accountablePersonId) ?? null;
  // `accountablePersonId` is a personId, so the People register is the right map to resolve it
  // through. A stored id that no longer resolves means the person record was deleted — say so
  // instead of rendering an empty box or printing the raw id.
  const personMissing = !!oversight.accountablePersonId && !selectedPerson && people.length > 0;

  const vendorRows = useRepeatableRows<VendorRecord>({
    stored: useCase.vendors,
    makeBlank: blankVendor,
    isBlank: isBlankVendor,
    onChange: vendors => update({ vendors }),
  });

  const transferRows = useRepeatableRows<TransferRecord>({
    stored: useCase.transfers,
    makeBlank: blankTransfer,
    isBlank: isBlankTransfer,
    onChange: transfers => update({ transfers }),
  });

  const frequency = useCase.reviewFrequency || '';
  const suggested = SUGGESTED_REVIEW_FREQUENCY[classification];

  /** Choosing a cadence seeds the first date if none is set, using the same arithmetic the
   *  server uses at approval. An existing date is never overwritten — a chosen review date is
   *  a commitment, not a derived value. */
  const setFrequency = (next: ReviewFrequency) => {
    if (next && !(useCase.nextReviewDate || '').trim()) {
      update({ reviewFrequency: next, nextReviewDate: addInterval(todayIso(), next) });
      return;
    }
    update({ reviewFrequency: next });
  };

  const oversightSatisfied = !!oversight.accountablePersonId.trim() && hasAnyOversight(oversight);

  return (
    <div>
      <p className="np-sub mb-4">
        Who can stop this, who supplies it, and what happens after it goes live. How much of
        this is required depends on the tier you chose on step 7 — a minimal-risk tool is asked
        for almost none of it.
      </p>

      {/* ── 1 — Human oversight ─────────────────────────────────────────────── */}
      <div className={`np-card mb-4${isHighRisk && !oversightSatisfied ? ' np-card-accent-warning' : ''}`}>
        <div className="np-card-head">
          <span className={`np-icon-badge np-tone-${isHighRisk ? 'warning' : 'primary'}`}>
            <i className="ri-user-star-line"></i>
          </span>
          <h2>Human oversight</h2>
          {isHighRisk ? (
            <span className="np-chip np-tone-warning">Required — high risk</span>
          ) : isMinimal ? (
            <span className="np-chip np-tone-neutral">Optional at this tier</span>
          ) : (
            <span className="np-chip np-tone-info">Recommended</span>
          )}
          {isMinimal && (
            <button
              type="button"
              className="np-btn np-btn-ghost np-btn-sm"
              onClick={() => setOversightOpen(open => !open)}
              aria-expanded={oversightOpen}
            >
              <i className={`ri-arrow-${oversightOpen ? 'up' : 'down'}-s-line`}></i>
              {oversightOpen ? 'Hide' : 'Add anyway'}
            </button>
          )}
        </div>

        {oversightOpen ? (
          <div className="np-card-body">
            <p className="np-sub mt-0 mb-3">
              {isHighRisk
                ? 'A high-risk system needs a named human who is accountable for it (Article 26(2)) and at least one real way to intervene (Article 14). Both are enforced when you submit.'
                : 'Not enforced at this tier, but this is the block that makes an incident survivable. Fill in what is actually true today, not what the policy says should be true.'}
            </p>

            <fieldset className="mb-4">
              <legend className="np-field-caption">
                What can a human actually do?
                {isHighRisk && <Req />}
              </legend>
              {HUMAN_OVERSIGHT_CONTROLS.map(control => {
                const value = !!oversight[control.field];
                const inputId = `ai-oversight-${control.field}`;
                return (
                  <div className="d-flex align-items-start gap-2 mb-3" key={control.field}>
                    <label className="np-switch mb-0 mt-1" htmlFor={inputId}>
                      <input
                        type="checkbox"
                        id={inputId}
                        checked={value}
                        onChange={e => patchOversight({ [control.field]: e.target.checked })}
                      />
                      <span className="np-switch-track"></span>
                      <span className="np-switch-thumb"></span>
                    </label>
                    <div>
                      <label htmlFor={inputId} className="mb-0 d-flex align-items-center gap-2 flex-wrap">
                        <i className={`${control.icon} text-${value ? control.tone : 'muted'}`}></i>
                        {control.label}
                      </label>
                      <p className="np-hint m-0">{control.description}</p>
                    </div>
                  </div>
                );
              })}

              {isHighRisk && !hasAnyOversight(oversight) && (
                <div className="np-banner np-tone-warning" role="alert">
                  <i className="ri-error-warning-line"></i>
                  <div>
                    A high-risk system with no approval gate, no override and no kill switch is the
                    exact state Article 14 exists to prevent. Record at least one, or reconsider the
                    classification on step 7.
                  </div>
                </div>
              )}
            </fieldset>

            <div className="np-field">
              <label htmlFor="ai-oversight-person">
                Accountable person
                {isHighRisk && <Req />}
              </label>
              <Select
                inputId="ai-oversight-person"
                options={personOptions}
                value={selectedPerson}
                onChange={opt => patchOversight({ accountablePersonId: opt?.value || '' })}
                placeholder="Search for a person…"
                isClearable
                styles={reactSelectStyles()}
              />
              <p className="np-hint">
                A named individual, not a team or a mailbox — the point of the field is that
                somebody can be asked. This is the person who answers for the system's outcomes,
                which may or may not be the builder from step 2.
              </p>
              {personMissing && (
                <div className="np-banner np-tone-warning" role="alert">
                  <i className="ri-user-unfollow-line"></i>
                  <div>
                    The person recorded here (<span className="np-tabular">{oversight.accountablePersonId}</span>)
                    is no longer in the People register. Choose someone current — an accountable
                    person who has left is the same as none.
                  </div>
                </div>
              )}
            </div>

            <div className="np-field mb-0">
              <label htmlFor="ai-oversight-escalation">Escalation path</label>
              <textarea
                id="ai-oversight-escalation"
                className="np-textarea"
                rows={3}
                placeholder="e.g. Reviewer flags it to the support lead, who can pause the assistant from the admin console. Anything affecting a customer decision goes to the DPO the same day, and to the AI governance group at its next meeting."
                value={oversight.escalationPath}
                onChange={e => patchOversight({ escalationPath: e.target.value })}
              />
              <p className="np-hint">
                Who is told, how, and how fast — when oversight is actually exercised. Name the
                route, not the org chart.
              </p>
            </div>
          </div>
        ) : (
          <div className="np-card-body">
            <p className="np-hint m-0">
              At minimal risk the Act asks for no specific oversight arrangement, so this is left
              empty rather than filled in for the sake of it. Add it anyway if a human gate,
              override or kill switch genuinely exists — it is worth recording.
            </p>
          </div>
        )}
      </div>

      {/* ── 2 — Vendors and models ──────────────────────────────────────────── */}
      <div className="np-card mb-4">
        <div className="np-card-head">
          <span className="np-icon-badge np-tone-primary">
            <i className="ri-shopping-bag-3-line"></i>
          </span>
          <h2>Vendors &amp; models</h2>
          <span className="np-chip np-tone-neutral">One row per supplier</span>
        </div>
        <div className="np-card-body">
          <p className="np-sub mt-0 mb-3">
            Which model, from whom, hosted where, under what contract. Leave this empty if the
            system is entirely in-house — but a self-hosted open-weight model still has a model
            name worth recording.
          </p>

          {vendorRows.rows.map((v, i) => (
            <RepeatableRowCard
              key={v.id}
              label="Vendor"
              index={i}
              accent={v.trainsOnOurData ? 'warning' : undefined}
              onRemove={vendorRows.canRemove ? () => vendorRows.remove(v.id) : undefined}
              chips={
                <>
                  {v.hostingRegion.trim() && (
                    <span className="np-chip np-tone-neutral">{v.hostingRegion}</span>
                  )}
                  {v.trainsOnOurData && (
                    <span className="np-chip np-tone-warning">Trains on our data</span>
                  )}
                </>
              }
            >
              <div className="row g-3 mb-3">
                <div className="col-12 col-md-6">
                  <div className="np-field mb-0">
                    <label htmlFor={`ai-vendor-name-${v.id}`}>Vendor</label>
                    <input
                      id={`ai-vendor-name-${v.id}`}
                      type="text"
                      className="np-input"
                      placeholder="e.g. OpenAI, Microsoft, self-hosted"
                      value={v.name}
                      onChange={e => vendorRows.patch(v.id, { name: e.target.value })}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="np-field mb-0">
                    <label htmlFor={`ai-vendor-model-${v.id}`}>Model and version</label>
                    <input
                      id={`ai-vendor-model-${v.id}`}
                      type="text"
                      className="np-input"
                      placeholder="e.g. gpt-4o (2024-08-06), Llama 3.1 70B"
                      value={v.modelName}
                      onChange={e => vendorRows.patch(v.id, { modelName: e.target.value })}
                    />
                    <p className="np-hint">
                      The version matters: a silent model upgrade is a material change to this
                      system.
                    </p>
                  </div>
                </div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-12 col-md-4">
                  <div className="np-field mb-0">
                    <label htmlFor={`ai-vendor-region-${v.id}`}>Hosting region</label>
                    <input
                      id={`ai-vendor-region-${v.id}`}
                      type="text"
                      className="np-input"
                      list={HOSTING_REGION_LIST_ID}
                      placeholder="Where does inference run?"
                      value={v.hostingRegion}
                      onChange={e => vendorRows.patch(v.id, { hostingRegion: e.target.value })}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="np-field mb-0">
                    <label htmlFor={`ai-vendor-dpa-${v.id}`}>DPA reference</label>
                    <input
                      id={`ai-vendor-dpa-${v.id}`}
                      type="text"
                      className="np-input"
                      placeholder="e.g. MSA 2025-114, Annex 2"
                      value={v.dpaReference}
                      onChange={e => vendorRows.patch(v.id, { dpaReference: e.target.value })}
                    />
                    <p className="np-hint">A reference, not just &quot;yes&quot;.</p>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="np-field mb-0">
                    <label htmlFor={`ai-vendor-expiry-${v.id}`}>Contract expiry</label>
                    <input
                      id={`ai-vendor-expiry-${v.id}`}
                      type="date"
                      className="np-input"
                      value={v.contractExpiry}
                      onChange={e => vendorRows.patch(v.id, { contractExpiry: e.target.value })}
                    />
                    <p className="np-hint">Leave empty if open-ended.</p>
                  </div>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-12 col-md-5">
                  <span className="np-field-caption">Does the vendor train on our data?</span>
                  <div className="np-pill-toggle-group">
                    <button
                      type="button"
                      className={`np-pill-toggle${v.trainsOnOurData ? ' active np-tone-warning' : ''}`}
                      aria-pressed={v.trainsOnOurData}
                      onClick={() => vendorRows.patch(v.id, { trainsOnOurData: true })}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`np-pill-toggle${!v.trainsOnOurData ? ' active np-tone-neutral' : ''}`}
                      aria-pressed={!v.trainsOnOurData}
                      onClick={() => vendorRows.patch(v.id, { trainsOnOurData: false })}
                    >
                      No
                    </button>
                  </div>
                  <p className="np-hint">
                    Check the contract rather than the marketing page. &quot;No&quot; is stored as
                    an answer, so it should be one.
                  </p>
                </div>
                <div className="col-12 col-md-7">
                  <div className="np-field mb-0">
                    <label htmlFor={`ai-vendor-subs-${v.id}`}>Sub-processors</label>
                    <textarea
                      id={`ai-vendor-subs-${v.id}`}
                      className="np-textarea"
                      rows={2}
                      placeholder="e.g. Azure OpenAI (EU) for inference, Datadog (US) for logs"
                      value={v.subProcessors}
                      onChange={e => vendorRows.patch(v.id, { subProcessors: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {isOrphanVendor(v) && (
                <div className="np-banner np-tone-warning mt-3" role="alert">
                  <i className="ri-error-warning-line"></i>
                  <div>
                    This row has no vendor and no model name, so it will not be saved. Name one of
                    the two, or clear the row.
                  </div>
                </div>
              )}
            </RepeatableRowCard>
          ))}

          <datalist id={HOSTING_REGION_LIST_ID}>
            {HOSTING_REGION_SUGGESTIONS.map(region => (
              <option key={region} value={region} />
            ))}
          </datalist>

          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="np-btn np-btn-ghost np-btn-sm"
              onClick={() => vendorRows.add()}
              disabled={!vendorRows.canAdd}
            >
              <i className="ri-add-line"></i>Add vendor
            </button>
            {!vendorRows.canAdd && (
              <span className="np-hint m-0">
                Name the vendor or the model above to add another.
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── 3 — Transfers ───────────────────────────────────────────────────── */}
      <div className="np-card mb-4">
        <div className="np-card-head">
          <span className="np-icon-badge np-tone-primary">
            <i className="ri-global-line"></i>
          </span>
          <h2>Transfers outside the EEA</h2>
          <span className="np-chip np-tone-neutral">Chapter V</span>
        </div>
        <div className="np-card-body">
          <p className="np-sub mt-0 mb-3">
            If any vendor above processes personal data outside the EEA, record the transfer and
            its lawful basis. Same fields, same vocabulary and same rule as the Record of
            Processing Activities. Leave empty if there are none.
          </p>

          {transferRows.rows.map((t, i) => (
            <RepeatableRowCard
              key={t.id}
              label="Transfer"
              index={i}
              accent={t.mechanism === 'none' ? 'danger' : 'success'}
              onRemove={transferRows.canRemove ? () => transferRows.remove(t.id) : undefined}
              chips={
                t.country.trim() ? (
                  <span className={`np-chip np-tone-${TRANSFER_MECHANISM_TONE[t.mechanism]}`}>
                    {TRANSFER_MECHANISM_LABELS[t.mechanism]}
                  </span>
                ) : undefined
              }
            >
              <div className="row g-3 mb-3">
                <div className="col-12 col-md-6">
                  <div className="np-field mb-0">
                    <label htmlFor={`ai-transfer-country-${t.id}`}>Country or territory</label>
                    <input
                      id={`ai-transfer-country-${t.id}`}
                      type="text"
                      className="np-input"
                      placeholder="e.g. United States"
                      value={t.country}
                      onChange={e => transferRows.patch(t.id, { country: e.target.value })}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="np-field mb-0">
                    <label htmlFor={`ai-transfer-recipient-${t.id}`}>Recipient</label>
                    <input
                      id={`ai-transfer-recipient-${t.id}`}
                      type="text"
                      className="np-input"
                      placeholder="Who receives it there?"
                      value={t.recipient}
                      onChange={e => transferRows.patch(t.id, { recipient: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <div className="np-field mb-0">
                    <label htmlFor={`ai-transfer-mechanism-${t.id}`}>Transfer mechanism</label>
                    <select
                      id={`ai-transfer-mechanism-${t.id}`}
                      className="np-select"
                      value={t.mechanism}
                      onChange={e =>
                        transferRows.patch(t.id, {
                          mechanism: e.target.value as TransferMechanism,
                        })
                      }
                    >
                      {TRANSFER_MECHANISMS.map(m => (
                        <option key={m} value={m}>
                          {TRANSFER_MECHANISM_LABELS[m]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="np-field mb-0">
                    <label htmlFor={`ai-transfer-safeguards-${t.id}`}>Safeguards</label>
                    <input
                      id={`ai-transfer-safeguards-${t.id}`}
                      type="text"
                      className="np-input"
                      placeholder="e.g. SCCs module 2 (2021), plus a transfer impact assessment dated 2026-03"
                      value={t.safeguards}
                      onChange={e => transferRows.patch(t.id, { safeguards: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {t.mechanism === 'none' && !isBlankTransfer(t) && (
                <div className="np-banner np-tone-danger mt-3" role="alert">
                  <i className="ri-error-warning-line"></i>
                  <div>
                    This transfer has no Chapter V mechanism recorded. You can still submit for
                    review, but the reviewer cannot approve the use case until one is chosen — a
                    transfer with no lawful basis is the most reportable state this record can be
                    in, and it is deliberately left visible so it can be refused rather than
                    quietly blocked here.
                  </div>
                </div>
              )}
            </RepeatableRowCard>
          ))}

          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="np-btn np-btn-ghost np-btn-sm"
              onClick={() => transferRows.add()}
              disabled={!transferRows.canAdd}
            >
              <i className="ri-add-line"></i>Add a transfer
            </button>
            {!transferRows.canAdd && (
              <span className="np-hint m-0">
                Name the country or the recipient above to add another.
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── 4 — Monitoring and review ───────────────────────────────────────── */}
      <div className="np-card mb-4">
        <div className="np-card-head">
          <span className="np-icon-badge np-tone-primary">
            <i className="ri-pulse-line"></i>
          </span>
          <h2>After it goes live</h2>
          <span className="np-chip np-tone-neutral">Art 72</span>
        </div>
        <div className="np-card-body">
          <p className="np-sub mt-0 mb-3">
            The outcomes on step 4 track whether this delivers value. These three track whether it
            is still safe — a different question, and the one nothing in this register asked
            before.
          </p>

          {MONITORING_FIELDS.map(field => (
            <div className="np-field" key={field.field}>
              <label htmlFor={`ai-monitoring-${field.field}`}>{field.label}</label>
              <textarea
                id={`ai-monitoring-${field.field}`}
                className="np-textarea"
                rows={3}
                placeholder={field.placeholder}
                value={monitoring[field.field]}
                onChange={e => patchMonitoring({ [field.field]: e.target.value })}
              />
              <p className="np-hint">{field.guidance}</p>
            </div>
          ))}

          <hr className="np-divider" />

          <div className="row g-3">
            <div className="col-12 col-md-6">
              <div className="np-field mb-0">
                <label htmlFor="ai-review-frequency">Review cadence</label>
                <select
                  id="ai-review-frequency"
                  className="np-select"
                  value={frequency}
                  onChange={e => setFrequency(e.target.value as ReviewFrequency)}
                >
                  <option value="">{REVIEW_FREQUENCY_LABELS['']}</option>
                  {REVIEW_FREQUENCIES.map(f => (
                    <option key={f} value={f}>
                      {REVIEW_FREQUENCY_LABELS[f]}
                    </option>
                  ))}
                </select>
                {suggested && suggested !== frequency && (
                  <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                    <span className="np-hint m-0">Suggested for this tier</span>
                    <button
                      type="button"
                      className="np-pill-toggle"
                      onClick={() => setFrequency(suggested)}
                    >
                      {REVIEW_FREQUENCY_LABELS[suggested]}
                    </button>
                  </div>
                )}
                <p className="np-hint">
                  A suggestion, not a rule — the cadence is a policy decision and this field does
                  not make it for you.
                </p>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="np-field mb-0">
                <label htmlFor="ai-next-review">Next review date</label>
                <input
                  id="ai-next-review"
                  type="date"
                  className="np-input"
                  value={useCase.nextReviewDate || ''}
                  onChange={e => update({ nextReviewDate: e.target.value })}
                />
                <p className="np-hint">
                  {frequency
                    ? 'Seeded from the cadence when you chose it, and yours to change. If you leave it empty, approval will seed it from the cadence.'
                    : 'Optional on its own — pick a cadence and this fills itself in.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step8Oversight;
